// src/components/TopBar.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col, Navbar, Nav, Form } from "react-bootstrap";
import { Link, useLocation, matchPath } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { getAllowedBranches } from "./auth/allowedBranches";

import {
  BsBoxArrowRight,
  BsInbox,
  BsArrowDownSquare,
  BsArrowUpSquare,
  BsClipboardData,
} from "react-icons/bs";
import { FaChevronDown, FaFileInvoice, FaFileAlt, FaCog } from "react-icons/fa";
import appLogo from "../assets/app-logo.png";
import { authFetch } from "./utils/authFetch"; // TopBar.jsx e în src/components/
import versionInfo from "../version.json";
import HelpModal from "./common/HelpModal";
import TreeHelp from "./common/TreeHelp";

// -------------------- STATIC CONFIG (fără accounts aici!) --------------------
const topMenuConfig = [
  {
    key: "salarii",
    label: "Salarii",
    columns: [
      {
        title: "Salarii",
        icon: BsClipboardData,
        links: [{ label: "Situație lunară", path: "/salarii" }],
      },
    ],
  },
  {
    key: "nomenclatoare",
    label: "Nomenclatoare",
    columns: [
      {
        title: "Produse",
        icon: BsArrowDownSquare,
        links: [
          { label: "Grupa de produse", path: "/grupa_produse" },
          { label: "Coduri de produse", path: "/bxpart" },
          { label: "Coduri manoperă", path: "/coduri-manopera" },
        ],
      },
      {
        title: "Contacte",
        icon: BsInbox,
        links: [
          { label: "Contacte", path: "/contacte" },
          { label: "Angajați", path: "/angajati" },
          {
            label: "Sume cuvenite angajaților",
            path: "/rapoarte/manopera-angajati",
          },
        ],
      },
      {
        title: "Tabele",
        icon: BsInbox,
        links: [
          { label: "Cota TVA", path: "/bxvat" },
          { label: "Unitati de masura", path: "/bxums" },
          { label: "Valute", path: "/bxvaluta" },
          { label: "Agent", path: "/agent" },
          { label: "Gestiune", path: "/bxgestiune" },
          { label: "Centre de cost", path: "/bxcentrucost" },
          { label: "Branches", path: "/branches" },
          { label: "Cataloage prețuri", path: "/catalog" },
        ],
      },
    ],
  },
  {
    key: "intrari",
    label: "Intrări",
    columns: [
      {
        title: "Documente Intrare",
        icon: BsArrowDownSquare,
        links: [
          // ✅ aici injectăm dinamic conturile din /accounts/menu
          { label: "NIR / Recepții", path: "/nir" },
        ],
      },
      {
        title: "Furnizori & Plăți",
        icon: BsInbox,
        links: [
          { label: "Furnizori neplatiti", path: "/intrare/rest/sumar" },
          { label: "Registru Plăți & Încasări", path: "/cash" },
        ],
      },
      {
        title: "Utilitare",
        icon: FaCog,
        links: [{ label: "Rapoarte intrări", path: "/rapoarte/intrari" }],
      },
    ],
  },
  {
    key: "iesiri",
    label: "Ieșiri",
    columns: [
      {
        title: "Vânzări",
        icon: BsArrowUpSquare,
        links: [
          // ✅ aici injectăm dinamic conturile din /accounts/menu   -clienti
          { label: "Vânzare cu casa de marcat", path: "/iesire/casa-marcat/new" },
          { label: "Bonuri casă de marcat", path: "/iesire/casa-marcat" },
        ],
      },

      {
        title: "Clienți & Încasări",
        icon: FaFileInvoice,
        links: [
          { label: "Registru Plăți & Încasări", path: "/cash" },
          { label: "Rapoarte ieșiri", path: "/rapoarte/iesiri" },
        ],
      },
    ],
  },

  {
    key: "casa",
    label: "Casa/Banca",
    columns: [
      {
        title: "Conturi Casa/Bancă",
        icon: FaFileAlt,
        links: [],
      },
      {
        title: "Rapoarte Casa/Bancă",
        icon: BsClipboardData,
        links: [
          { label: "Registru Plăți & Încasări", path: "/cash" },
          { label: "Raport Zilnic Casa/Bancă", path: "/rapoarte/casa-zilnic" },
          { label: "Rapoarte casa/bancă", path: "/rapoarte/casa" },
        ],
      },
    ],
  },
  {
    key: "stoc",
    label: "Stoc",
    columns: [
      {
        title: "Miscari",
        icon: BsInbox,
        links: [
          { label: "Procese verbale", path: "/proces-verbal" },
          { label: "Bon consum-", path: "/bon-consum" },
          { label: "Bon transfer", path: "/bon-transfer" },
        ],
      },
      {
        title: "Alte documente",
        icon: BsInbox,
        links: [
          { label: "Inventar", path: "/bxinventar" },
          { label: "Mijloace Fixe", path: "/mijloace-fixe" },
          { label: "Grupe Mijloace Fixe", path: "/bxmfcod" },
          { label: "Tipuri Mijloace Fixe", path: "/bxtipmf" },
          { label: "Clasificare Mijloace Fixe", path: "/bxcodclasificare" },
          { label: "Obiecte de Inventar", path: "/obinv" },
          { label: "Transferuri Ob. Inv.", path: "/obinv/transfer" },
          { label: "Casări Ob. Inv.", path: "/obinv/casare" },
          { label: "Raport Stoc Angajați", path: "/obinv/raport-stoc" },
          { label: "Istoric Mișcări (Linii)", path: "/obinv/istoric-miscari" },
        ],
      },
      {
        title: "Rapoarte",
        icon: BsClipboardData,
        links: [
          { label: "Stoc", path: "/stock/curent" },
          { label: "Rapoarte stoc", path: "/rapoarte/stoc" },
        ],
      },
    ],
  },
  {
    key: "contor",
    label: "Service (Contor)",
    columns: [
      {
        title: "Contor",
        icon: FaFileAlt,
        links: [
          { label: "Sesizări & Tichete", path: "/contor/sesizari" },
          { label: "Echipamente", path: "/contor/echipamente" },
          { label: "Grupe Echipamente", path: "/contor/grupe" },
        ],
      },
      {
        title: "Simaus",
        icon: BsInbox,
        links: [
          { label: "Comenzi", path: "/contor/grupe" },
          { label: "Comenzi Clienți (Simaus)", path: "/comenzi/client/linii" },
        ],
      },
    ],
  },
  {
    key: "contabilitate",
    label: "Contabilitate",
    columns: [
      {
        title: "Înregistrări",
        icon: FaFileAlt,
        links: [
          { label: "Note simple", path: "/nc" },
          { label: "Lista note contabile", path: "/nc/lista" },
          { label: "Control repartizare pe centre", path: "/repartizari-centre" },
          { label: "Închide luna", path: "/inchide-luna" },
        ],
      },
      {
        title: "Declarații",
        icon: FaFileInvoice,
        links: [
          { label: "eFactura (SPV)", path: "/efactura" },
          { label: "Decont TVA (D300)", path: "/d300" },
          { label: "Declarația D394", path: "/d394" },
          { label: "Declarația SAF-T (D406)", path: "/d406" },
        ],
      },
      {
        title: "Conturi",
        icon: FaCog,
        links: [
          { label: "Plan de conturi", path: "/accounts" },

          { label: "Rapoarte contabilitate", path: "/rapoarte/contabilitate" },
        ],
      },
    ],
  },
  {
    key: "Configurare",
    label: "Configurare",
    columns: [
      {
        title: "Configurarea firmei",
        icon: FaCog,
        links: [
          { label: "Pregătirea firmei", path: "/configurare-ghidata" },
          { label: "Firma", path: "/firma/1" },
          { label: "Modul Distribuție apă", path: "/apa?tab=setari",superadminOnly:true },
          {
            label: "Superadmin (Panou Firme)",
            path: "/superadmin/dashboard",
            superadminOnly: true,
          },
          { label: "Useri", path: "/admin/user" },
          { label: "Drepturi", path: "/admin/drepturi" },
          { label: "Roluri", path: "/admin/roluri" },
          { label: "Plan de conturi", path: "/accounts" },
          { label: "Istoric Documente (Audit)", path: "/istoric" },
        ],
      },
      {
        title: "Magazin online",
        icon: FaCog,
        links: [
          { label: "Setări magazin", path: "/magazin-online" },
          { label: "Adaugă un articol din ERP", path: "/magazin-online/produse" },
          { label: "Lista produse existente în magazin", path: "/magazin-online/produse-existente" },
          { label: "Comenzi primite", path: "/magazin-online/comenzi" },
        ],
      },
      {
        title: "Import date initiale",
        icon: FaCog,
        links: [
          { label: "Import balanță", path: "/admin/import-balanta" },
          { label: "Import stoc inițial", path: "/admin/import-stoc" },
          { label: "Import facturi inițiale", path: "/admin/import-facturi" },
          { label: "Import mijloace fixe", path: "/admin/import-mf" },
        ],
      },
      {
        title: "Altele",
        icon: FaCog,
        links: [
          {
            label: "Corespondente centre pe cont cheltuiala sau venit",
            path: "/cont_cc_map",
          },
          { label: "Tip bon iesire", path: "/tip-bon-iesire" },
          { label: "Tip factura iesire", path: "/tip_factura_iesire" },
          { label: " Tip_factura_intrare ", path: "/tip_factura_intrare" },
          { label: "Doc_type_cfg", path: "/doc_type_cfg" },
        ],
      },
    ],
  },
];

// -------------------- HELPERS --------------------
function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

const buildIntrareLinksFromAccounts = (accounts) =>
  (accounts || []).map((a) => {
    console.log("ACCOUNT ITEM:", a);
    return {
      label: a.menu_label || a.name || a.code || `Cont ${a.id}`,
      path: `/intrare/intrare_list/${a.id}`,
      account_id: a.id,
      account_code: a.code || "",
      account_label: a.denumire || a.name || "",
    };
  });

// -------------------- COMPONENTS --------------------
function MegaMenu({
  open,
  columns,
  onClose,
  isDark,
  onMouseEnter,
  onMouseLeave,
  onItemSelect,
  onLinkSelect,
  user,
}) {
  const ref = useRef(null);
  useOnClickOutside(ref, onClose);

  if (!open) return null;

  return (
    <div
      ref={ref}
      className={`mega-menu ${isDark ? "menu-dark" : ""}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Container className="py-3">
        <Row>
          {columns.map((col, idx) => (
            <Col md={4} sm={6} xs={12} key={idx} className="mb-3">
              <div className="mega-col-title">
                {col.icon &&
                  React.createElement(col.icon, {
                    size: 16,
                    className: "me-2",
                  })}
                {col.title}
              </div>

              <ul className="list-unstyled mb-0 mt-2">
                {col.links
                  .filter(
                    (l) =>
                      !l.superadminOnly ||
                      (user?.is_superuser && user?.user_name === "superadmin"),
                  )
                  .map((l, i) => (
                    <li key={i}>
                      <Link
                        to={l.path}
                        className="mega-link"
                        onClick={() => {
                          onLinkSelect?.(l); // 👈 aici ai acces la l.account_id/code/label
                          onItemSelect?.();
                        }}
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
              </ul>
            </Col>
          ))}
        </Row>
      </Container>
    </div>
  );
}

const INTRARE_CTX_KEY = "intrare_ctx";

const handleMenuLinkSelect = (l) => {
  // doar pentru link-urile generate din conturi (au account_id)

  const id = Number(l?.account_id);
  if (!Number.isInteger(id) || id <= 0) return;

  const payload = {
    accountId: id,
    accountCode: l.account_code || "", // trebuie să existe pe link
    accountLabel: l.account_label || l.label || "",
  };

  const isIesire = l.path && l.path.includes("/iesire/");
  const storageKey = isIesire ? "iesire_ctx" : "intrare_ctx";
  localStorage.setItem(storageKey, JSON.stringify(payload));
};

function isItemActiveByUrl(item, pathname) {
  if (!item?.columns?.length) return false;

  for (const col of item.columns) {
    for (const l of col.links || []) {
      if (!l?.path) continue;

      const pathWithoutQuery = l.path.split("?")[0];

      // matchPath suporta ?i rute cu parametri (ex: /cash/:id)
      const m = matchPath({ path: pathWithoutQuery, end: false }, pathname);
      if (m) return true;

      // fallback simplu pentru rute care sunt prefix
      if (pathname.startsWith(pathWithoutQuery)) return true;
    }
  }
  return false;
}

function TopBar({ user, onLogout }) {
  const [showHelp, setShowHelp] = useState(false);
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const [isMenuDark, setIsMenuDark] = useState(
    () => localStorage.getItem("is_menu_dark") === "true",
  );

  const toggleMenuDark = (e) => {
    const checked = e.target.checked;
    setIsMenuDark(checked);
    localStorage.setItem("is_menu_dark", String(checked));
  };
  const [openKey, setOpenKey] = useState(null);
  const closeTimer = useRef(null);
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    setIsNavExpanded(false);
    setOpenKey(null);
  }, [location.pathname, location.search]);

  // ✅ conturile pentru “Intrări -> Documente Intrare”
  const [menuAccounts, setMenuAccounts] = useState([]);
  // ✅ conturile de casă și bancă (51, 53)
  const [cashAccounts, setCashAccounts] = useState([]);

  const { activeBranchId, setActiveBranchId } = useAuth();
  const [branchesList, setBranchesList] = useState([]);

  useEffect(() => {
    if (!user) return;
    authFetch("/branches/?limit=1000")
      .then((res) => {
        if (res && res.items) {
          setBranchesList(res.items);
        }
      })
      .catch((e) => console.error("Error loading branches in TopBar:", e));
  }, [user]);

  const allowedBranches = useMemo(() => {
    return getAllowedBranches(user, branchesList);
  }, [branchesList, user]);

  const [companies, setCompanies] = useState([]);
  const [activeTenant, setActiveTenant] = useState(
    () => localStorage.getItem("active_tenant") || "",
  );
  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
    if (user?.user_name === "superadmin") {
      authFetch("/auth/companies")
        .then((res) => {
          setCompanies(Array.isArray(res) ? res : []);
        })
        .catch((e) => {
          console.error("Failed to load companies:", e);
        });
    }
  }, [user]);

  const [appVariant, setAppVariant] = useState("STANDARD");
  const [wooConfigured,setWooConfigured]=useState(false);
  useEffect(()=>{let alive=true;setWooConfigured(false);if(user)authFetch('/woocommerce/config').then(r=>{if(alive)setWooConfigured(!!(r.configured||r.managed));}).catch(()=>{});return()=>{alive=false;};},[user,activeTenant]);
  const [erpEnabled,setErpEnabled]=useState(true),[shopEnabled,setShopEnabled]=useState(true);
  useEffect(()=>{
    let active=true;
    const refresh=()=>authFetch('/company-modules').then(r=>{if(active){setErpEnabled(r.erp_enabled);setShopEnabled(r.shop_enabled);setAppVariant(r.app_variant||'LITE');setSantierVisible(Number(r.santier_visible)===1);}}).catch(()=>{});
    if(user)refresh();
    window.addEventListener('company-modules-changed',refresh);
    return()=>{active=false;window.removeEventListener('company-modules-changed',refresh);};
  },[user,activeTenant]);
  const [santierVisible, setSantierVisible] = useState(false);
  const [waterVisible,setWaterVisible] = useState(false);
  useEffect(()=>{
    let active=true;
    setWaterVisible(false);
    const refresh=()=>authFetch('/apa/config').then(r=>{if(active)setWaterVisible(!!r.enabled);}).catch(()=>{if(active)setWaterVisible(false);});
    if(user)refresh();
    window.addEventListener('water-module-changed',refresh);
    return()=>{active=false;window.removeEventListener('water-module-changed',refresh);};
  },[user,activeTenant]);

  useEffect(() => {
    if (!user) return;
    authFetch("/firma")
      .then((res) => {
        if (res && res.items && res.items.length > 0) {
          setCompanyName(res.items[0].bxdenumire || "");
          setAppVariant(res.items[0].app_variant || "STANDARD");
          setSantierVisible(Number(res.items[0].santier_visible) === 1);
        }
      })
      .catch((e) => {
        console.error("Failed to load company name:", e);
      });
  }, [user, activeTenant]);

  const handleTenantChange = (e) => {
    const val = e.target.value;
    if (val) {
      localStorage.setItem("active_tenant", val);
    } else {
      localStorage.removeItem("active_tenant");
    }
    localStorage.removeItem("active_branch_id");
    setActiveTenant(val);
    window.location.reload();
  };

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openNow = (key) => {
    clearCloseTimer();
    setOpenKey(key);
  };

  const closeSoon = () => {
    clearCloseTimer();
    setOpenKey(null);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpenKey(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ✅ load dynamic conturi din /accounts/menu și /accounts/cash-menu
  useEffect(() => {
    let active = true;
    const refreshAccountMenu = async () => {
      try {
        const acc = await authFetch("/accounts/menu");
        if (active) setMenuAccounts(Array.isArray(acc) ? acc : []);
      } catch (e) {
        console.error("Nu pot încărca conturile din /accounts/menu:", e);
        if (active) setMenuAccounts([]);
      }
    };
    refreshAccountMenu();
    window.addEventListener("accounts-menu-changed", refreshAccountMenu);

    (async () => {
      try {
        const acc = await authFetch("/accounts/cash-menu");
        setCashAccounts(Array.isArray(acc) ? acc : []);
      } catch (e) {
        console.error("Nu pot încărca conturile din /accounts/cash-menu:", e);
        setCashAccounts([]);
      }
    })();
    return () => {
      active = false;
      window.removeEventListener("accounts-menu-changed", refreshAccountMenu);
    };
  }, []);

  const MENU_ROUTE = {
    FURNIZORI: { to: "/intrare", key: "ctx:/intrare:bxcont_id" },
    CLIENTI: { to: "/iesire", key: "ctx:/iesire:bxcont_id" },
    CASA: { to: "/casa", key: "ctx:/casa:bxcont_id" },
    BANCA: { to: "/banca", key: "ctx:/banca:bxcont_id" },
    // adaugi ușor: NIR, BON_CONSUM, etc.
  };

  // helper: generează link-uri dinamice din accounts
  function buildLinksFromAccounts(menuAccounts, menu_group) {
    const cfg = MENU_ROUTE[menu_group];
    if (!cfg) return [];

    return (menuAccounts || [])
      .filter(
        (a) =>
          String(a.menu_group || "").toUpperCase() ===
          String(menu_group).toUpperCase(),
      )
      .sort((a, b) => (a.menu_order ?? 0) - (b.menu_order ?? 0))
      .map((a) => {
        const g = String(a.menu_group || "").toUpperCase();
        const base =
          g === "CLIENTI" ? "/iesire/iesire_list" : "/intrare/intrare_list";

        return {
          label: a.menu_label || `${a.code} • ${a.name}`,
          path: `${base}/${a.id}${(g === "CLIENTI" && String(a.code || "").startsWith("418")) || (g === "FURNIZORI" && String(a.code || "").startsWith("408")) ? "?tip_document=Aviz" : ""}`,
          account_id: a.id,
          storage_key: cfg.key,

          account_code: a.code || "",
          account_label: a.menu_label || a.name || "",
        };
      });
  }

  // ✅ final menu = static + inject conturi în coloane + filter by app_variant
  const finalMenuConfig = useMemo(() => {
    let base = topMenuConfig.map((x) => ({ ...x }));
    if(waterVisible&&(user?.is_superuser||user?.perms?.includes('APA_READ'))){
      base.push({key:'apa',label:'Distribuție apă',columns:[{title:'Distribuție apă',icon:FaFileAlt,links:[
        {label:'Citiri apometre',path:'/apa?tab=citiri'},
        {label:'Contracte clienți',path:'/apa?tab=contracte'},
        {label:'Apometre',path:'/apa?tab=apometre'},
        {label:'Facturări',path:'/apa?tab=facturari'},
        {label:'Străzi',path:'/apa?tab=strazi'}]}]});
    }

    // 1. Remove separate 'contor' menu from the list of tabs
    base = base.filter((x) => x.key !== "contor");
    if ((appVariant === "SANTIER" || santierVisible) &&
        (user?.is_superuser || (user?.perms?.includes("SANTIER_READ") && user?.perms?.includes("ALL_BRANCHES")))) {
      const index = base.findIndex(x => x.key === "stoc");
      base.splice(index < 0 ? base.length : index + 1, 0, {
        key: "santier", label: "Șantier", columns: [{ title: "Șantier", icon: FaFileAlt,
          links: [{ label: "Obiective și devize", path: "/santier" }, { label: "Rapoarte zilnice de lucru", path: "/santier?tab=rapoarte" }, { label: "Necesar de aprovizionare", path: "/santier?tab=aprovizionare" }, { label: "Situații de lucrări", path: "/santier?tab=situatii" }, { label: "Profitabilitate pe obiectiv", path: "/santier?tab=profitabilitate" }] }],
      });
    }

    // 2. Show the dedicated menu for the active optional module.
    const isLite = ['LITE','APA'].includes((appVariant || "").toUpperCase());
    if (!isLite && appVariant !== "SANTIER") {
      const stocIndex = base.findIndex((x) => x.key === "stoc");

      let moduleMenu = null;

      if (appVariant === "SERVICE_AUTO") {
        moduleMenu = {
          key: "service_auto",
          label: "Service auto",
          columns: [
            {
              title: "Service auto",
              icon: FaFileAlt,
              links: [
                {
                  label: "Comenzi Clienți (Auto)",
                  path: "/comenzi/client/linii",
                },
                {
                  label: "Devize (Comenzi Auto)",
                  path: "/comenzi/intern/linii",
                },
                {
                  label: "Sume cuvenite angajaților",
                  path: "/rapoarte/manopera-angajati",
                },

                { label: "Coduri manoperă", path: "/coduri-manopera" },
                {
                  label: "Istoric ReparaE>ii / E~asiu",
                  path: "/rapoarte/istoric-reparatii",
                },
                {
                  label: "Profitabilitate pe Deviz",
                  path: "/rapoarte/profitabilitate",
                },
                {
                  label: "Istoric ReparaE>ii / E~asiu",
                  path: "/rapoarte/istoric-reparatii",
                },
                {
                  label: "Profitabilitate pe Deviz",
                  path: "/rapoarte/profitabilitate",
                },
                { label: "Rapoarte service", path: "/rapoarte/service" }, // Added generic route for future reports
              ],
            },
          ],
        };
      } else if (appVariant === "CONTOR") {
        moduleMenu = {
          key: "contor",
          label: "Contor",
          columns: [
            {
              title: "Contor",
              icon: FaFileAlt,
              links: [
                { label: "Generare facturi", path: "/contor/facturare-client" },
                { label: "Contracte", path: "/contor/contracte" },
                { label: "Transfer echipament", path: "/contor/transfer-echipament" },
                { label: "Sesizări & Tichete", path: "/contor/sesizari" },
                { label: "Echipamente", path: "/contor/echipamente" },
                { label: "Grupe Echipamente", path: "/contor/grupe" },
              ],
            },
          ],
        };
      } else {
        // PRODUCTION MENU
        const cols = [
          {
            title: "Producție",
            icon: BsArrowDownSquare,
            links: [
              { label: "Comenzi Clienți", path: "/comenzi/client/linii" },
              { label: "Comenzi Producție", path: "/comenzi-prod" },
              { label: "Rețete", path: "/retete" },
              { label: "Bon producție", path: "/bon-productie" },
              {
                label: "Manoperă și angajați — producție",
                path: "/rapoarte/productie",
              },

              { label: "Coduri manoperă", path: "/coduri-manopera-prod" },
              {
                label: "Necesar Aprovizionare",
                path: "/rapoarte/necesar-aprovizionare",
              },
              {
                label: "Rețetă și materiale transferate",
                path: "/rapoarte/consum-teoretic-real",
              },
              { label: "Rapoarte producție", path: "/rapoarte/productie" }, // Added generic route for future reports
            ],
          },
        ];

        if (appVariant === "SIMAUS") {
          cols.push({
            title: "Simaus",
            icon: BsInbox,
            links: [
              { label: "Comenzi", path: "/comenzi/intern/linii" },
              {
                label: "Istoric ReparaE>ii / E~asiu",
                path: "/rapoarte/istoric-reparatii",
              },
              {
                label: "Profitabilitate pe Deviz",
                path: "/rapoarte/profitabilitate",
              },

              {
                label: "Comenzi Clienți (Simaus)",
                path: "/comenzi/client/linii",
              },
            ],
          });
        }

        moduleMenu = {
          key: "productie",
          label: "Producție",
          columns: cols,
        };
      }

      if (moduleMenu) {
        if (stocIndex !== -1) {
          base.splice(stocIndex + 1, 0, moduleMenu);
        } else {
          base.push(moduleMenu);
        }
      }
    }

    // Inject dedicated Comenzi Clienti link under iesiri -> Clienți & Încasări
    base = base.map((x) => {
      if (x.key === "iesiri") {
        const cols = (x.columns || []).map((col) => {
          if (col.title === "Clienți & Încasări") {
            let label = "Comenzi Clienți";
            if (appVariant === "SIMAUS") label = "Comenzi Clienți (Simaus)";
            if (appVariant === "SERVICE_AUTO") label = "Comenzi Clienți (Auto)";
            if (appVariant === "CONTOR") label = "Comenzi Clienți (Contor)";

            const cleanLinks = (col.links || []).filter(
              (l) => l.path !== "/comenzi/client/linii",
            );
            return {
              ...col,
              links: [{ label, path: "/comenzi/client/linii" }, ...cleanLinks],
            };
          }
          return col;
        });
        return { ...x, columns: cols };
      }
      return x;
    });

    const injectLinksIntoSection = (
      sectionKey,
      columnTitle,
      linksToInject,
      replace = false,
    ) => {
      const idx = base.findIndex((x) => x.key === sectionKey);
      if (idx < 0) return;

      const section = { ...base[idx] };

      section.columns = (section.columns || []).map((col) => {
        if (col.title !== columnTitle) return col;

        return {
          ...col,
          links: replace
            ? linksToInject
            : [...linksToInject, ...(col.links || [])],
        };
      });

      base[idx] = section;
    };

    injectLinksIntoSection(
      "intrari",
      "Documente Intrare",
      buildLinksFromAccounts(menuAccounts, "FURNIZORI"),
    );

    injectLinksIntoSection(
      "iesiri",
      "Vânzări",
      buildLinksFromAccounts(menuAccounts, "CLIENTI"),
    );

    const cashLinks = (cashAccounts || []).map((a) => ({
      label: `${a.code} • ${a.name}`,
      path: `/cash?account_id=${a.id}`,
      account_id: a.id,
      account_code: a.code || "",
      account_label: a.name || "",
    }));

    injectLinksIntoSection("casa", "Conturi Casa/Bancă", cashLinks, true);

    base=base.filter(section=>erpEnabled||section.key==='Configurare').map(section=>({...section,columns:(section.columns||[]).filter(column=>shopEnabled||column.title!=='Magazin online')}));
    if(wooConfigured)base=base.map(section=>({...section,columns:(section.columns||[]).map(column=>column.title==='Magazin online'?{...column,title:'WooCommerce',links:[{label:'Legătura ERP–WooCommerce',path:'/magazin-online'},{label:'Publică produse din ERP',path:'/magazin-online/produse'},{label:'Poze și produse în WooCommerce',path:'/magazin-online/produse-existente'},{label:'Comenzi WooCommerce',path:'/magazin-online/comenzi'}]}:column)}));
    return base.filter(
      (section) =>
        section.key !== "salarii" ||
        user?.is_superuser ||
        (user?.perms?.includes("SALARII_READ") &&
          user?.perms?.includes("ALL_BRANCHES")),
    );
  }, [menuAccounts, cashAccounts, topMenuConfig, appVariant, santierVisible, waterVisible, erpEnabled,shopEnabled,wooConfigured,user]);

  //   adaug sectiune de ex
  //   injectLinksIntoSection(
  //   "rapoarte",
  //   "Statistici",
  //   buildLinksFromAccounts(menuAccounts, "RAPOARTE")
  // );

  const activeItem = finalMenuConfig.find((i) => i.key === openKey);
  const menuWrapperRef = useRef(null);
  useOnClickOutside(menuWrapperRef, () => {
    setOpenKey(null);
  });

  const activeMenuKey = useMemo(() => {
    const storedKey = sessionStorage.getItem("activeMenuKey");
    if (storedKey) {
      const activeItem = finalMenuConfig.find((i) => i.key === storedKey);
      if (activeItem && isItemActiveByUrl(activeItem, pathname)) {
        return storedKey;
      }
    }

    if (pathname.startsWith("/cash")) {
      return "casa";
    }

    for (const item of finalMenuConfig) {
      if (isItemActiveByUrl(item, pathname)) {
        return item.key;
      }
    }
    return null;
  }, [pathname, finalMenuConfig]);

  return (
    <div className="sticky-top" style={{ zIndex: 1040 }}>
      <Navbar
        expanded={isNavExpanded}
        onToggle={setIsNavExpanded}
        bg={isMenuDark ? "dark" : "light"}
        variant={isMenuDark ? "dark" : "light"}
        expand="md"
        className={`shadow-sm px-4 py-2 ${
          isMenuDark ? "bg-dark navbar-dark text-white" : "bg_light_blue"
        }`}
      >
        <div className="d-flex flex-column flex-shrink-0 me-3">
        <Navbar.Brand
          as={Link}
          to="/"
          aria-label="Swan-ERP — Dashboard"
          title="Deschide dashboard-ul"
          className="fw-bold d-flex align-items-center m-0"
          onClick={() => {
            setOpenKey(null);
            setIsNavExpanded(false);
          }}
        >
          <img src={appLogo} alt="" height="32" className="me-2" />
          <div className="d-flex align-items-baseline">
            <h4 className="mb-0">Swan-ERP</h4>
            <span
              className={
                isMenuDark ? "text-light opacity-75 ms-2" : "text-muted ms-2"
              }
              style={{ fontSize: "0.75rem", fontWeight: "normal" }}
            >
              v{versionInfo.version}
            </span>
          </div>
        </Navbar.Brand>
        <a
          href="https://eda.ro"
          target="_blank"
          rel="noopener noreferrer"
          className={isMenuDark ? "text-light" : "text-primary"}
          style={{ fontSize: "0.8rem", alignSelf: "flex-start", marginLeft: "40px" }}
          aria-label="eda.ro — se deschide într-o filă nouă"
        >
          eda.ro <span aria-hidden="true">↗</span>
        </a>
        </div>

        <Navbar.Toggle aria-controls="mobile-nav" className="border-0" />

        <Navbar.Collapse id="mobile-nav">
          {/* ===== Desktop (>= md): tab-uri orizontale pentru mega-menu ===== */}

          {/* ===== Desktop (>= md): tab-uri orizontale pentru mega-menu ===== */}
          <Nav className="ms-3 d-none d-md-flex align-items-center">
            {finalMenuConfig.map((item) => {
              const isActive = openKey
                ? openKey === item.key
                : activeMenuKey === item.key;

              return (
                <div key={item.key}>
                  <button
                    type="button"
                    className={
                      "btn btn-link text-decoration-none d-flex align-items-center " +
                      (isActive
                        ? "fw-bold text-primary"
                        : isMenuDark
                          ? "text-white"
                          : "text-dark")
                    }
                    onClick={() =>
                      setOpenKey((prev) =>
                        prev === item.key ? null : item.key,
                      )
                    }
                    aria-expanded={openKey === item.key}
                  >
                    {item.label}
                    <FaChevronDown
                      className={`ms-1 ${openKey === item.key ? "rotate-180" : ""}`}
                      size={12}
                    />
                  </button>
                </div>
              );
            })}
          </Nav>

          {/* ===== Mobil (< md): accordion vertical ===== */}
          <Nav className="d-md-none mt-2 w-100">
            <div className="w-100">
              {finalMenuConfig.map((item) => (
                <details
                  key={item.key}
                  className="mobile-menu-section border-bottom"
                >
                  <summary className="d-flex align-items-center justify-content-between px-3 py-3">
                    <span>{item.label}</span>
                    <FaChevronDown size={14} aria-hidden="true" />
                  </summary>
                  <div className="px-3 pt-2">
                    <Row>
                      {(item.columns || []).map((col, cidx) => (
                        <Col xs={12} key={cidx} className="mb-3">
                          <div className="d-flex align-items-center gap-2 fw-semibold mb-2">
                            {col.icon && (
                              <span className="text-muted">
                                {React.createElement(col.icon, { size: 16 })}
                              </span>
                            )}
                            <span>{col.title}</span>
                          </div>

                          <ul className="list-unstyled mb-0">
                            {(col.links || [])
                              .filter(
                                (l) =>
                                  !l.superadminOnly ||
                                  (user?.is_superuser && user?.user_name === "superadmin"),
                              )
                              .map((l, lidx) => (
                                <li key={lidx}>
                                  <a
                                    href={l.path}
                                    className="d-block py-1 text-decoration-none"
                                    onClick={() => {
                                      try {
                                        sessionStorage.setItem(
                                          "activeMenuKey",
                                          item.key,
                                        );
                                      } catch (error) {
                                        console.warn(
                                          "Nu pot salva meniul activ:",
                                          error,
                                        );
                                      }
                                      handleMenuLinkSelect(l);
                                      setIsNavExpanded(false);
                                      setOpenKey(null);
                                    }}
                                  >
                                    {l.label}
                                  </a>
                                </li>
                              ))}
                          </ul>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </details>
              ))}
            </div>
          </Nav>

          {/* Dreapta (profil + logout) */}
          <Nav className="ms-auto align-items-center mt-2 mt-md-0">
            {user?.user_name === "superadmin" && companies.length > 0 && (
              <select
                className="form-select form-select-sm me-3"
                style={{ width: 180 }}
                value={activeTenant}
                onChange={handleTenantChange}
              >
                <option value="">(Implicit - {user?.tenant_db_url})</option>
                {companies.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name || c.code}
                  </option>
                ))}
              </select>
            )}
            <Link
              to="/walkthrough"
              className="btn btn-outline-primary btn-sm me-3 text-nowrap d-none d-md-inline-block"
              style={{ fontWeight: "500" }}
            >
              📖 Ghid Walkthrough
            </Link>
            <button
              type="button"
              className="btn btn-outline-info btn-sm me-3 text-nowrap d-none d-md-inline-block"
              style={{ fontWeight: "500" }}
              onClick={() => setShowHelp(true)}
            >
              ❓ Ajutor Important
            </button>
            {allowedBranches.length > 1 && (
              <select
                className="form-select form-select-sm me-3 bg-white text-dark border-secondary"
                style={{ width: 180 }}
                value={activeBranchId || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setActiveBranchId(val ? Number(val) : null);
                  window.location.reload();
                }}
              >
                <option value="">(Toate branch-urile)</option>
                {allowedBranches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.code} — {b.name}
                  </option>
                ))}
              </select>
            )}
            <Form.Check
              type="switch"
              id="menu-dark-switch"
              label="DARK"
              className={`me-4 fw-semibold ${isMenuDark ? "text-light" : "text-dark"}`}
              checked={isMenuDark}
              onChange={toggleMenuDark}
            />
            <Navbar.Text className={`me-3 ${isMenuDark ? "text-light" : ""}`}>
              Conectat ca:{" "}
              <strong className={isMenuDark ? "text-white" : ""}>
                {user?.user_name || "-"}
              </strong>
              {companyName && ` (${companyName})`}
            </Navbar.Text>
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={onLogout}
            >
              <BsBoxArrowRight className="me-1" /> Deconectare
            </button>
          </Nav>
        </Navbar.Collapse>
      </Navbar>

      <div className="d-none d-md-block">
        <MegaMenu
          user={user}
          open={!!activeItem}
          columns={activeItem?.columns || []}
          onClose={() => setOpenKey(null)}
          isDark={isMenuDark}
          onMouseEnter={undefined}
          onMouseLeave={undefined}
          onItemSelect={() => setOpenKey(null)}
          onLinkSelect={(l) => {
            if (activeItem?.key) {
              sessionStorage.setItem("activeMenuKey", activeItem.key);
            }
            handleMenuLinkSelect(l);
          }}
        />
      </div>
      <HelpModal
        show={showHelp}
        onHide={() => setShowHelp(false)}
        title="Manual de Utilizare & Informații Importante"
        size="xl"
      >
        <TreeHelp defaultChapter="important" />
      </HelpModal>
    </div>
  );
}

export default TopBar;
